"use client";

import Link from "next/link";
import { notFound } from "next/navigation";
import { ChevronRight } from "lucide-react";
import { LegalFooter } from "@/components/LegalFooter";
import { AppHeader } from "@/components/layout/AppHeader";
import { getGuideManifest } from "@/lib/guides/manifests";
import type { GuideId, GuideSectionDef } from "@/lib/guides/types";

function SectionCard({ guideId, section }: { guideId: GuideId; section: GuideSectionDef }) {
  return (
    <Link
      href={`/guides/${guideId}/${section.id}`}
      className="group flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-[0_18px_48px_hsl(var(--foreground)/0.06)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-xl border border-primary/15 bg-primary/10 p-2.5 text-primary">
          {section.icon}
        </div>
        {section.badge ? (
          <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {section.badge}
          </span>
        ) : null}
      </div>
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{section.label}</h2>
        <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{section.shortDescription}</p>
      </div>
      <div className="mt-auto flex items-center gap-1.5 text-sm font-medium text-primary pt-3 border-t border-border/40">
        Open guide
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

export default function GuideIndex({ guideId }: { guideId: GuideId }) {
  const manifest = getGuideManifest(guideId);
  if (!manifest) notFound();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader back={{ href: "/guides", label: "Back to guides" }} />

      <div className="mx-auto max-w-5xl px-4 pt-12 pb-20 sm:px-6 lg:px-8">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.28em] text-primary/80 mb-3">Documentation</p>
          <h1 className="text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">{manifest.title}</h1>
          <p className="mt-4 text-base leading-7 text-muted-foreground max-w-2xl">{manifest.subtitle}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {manifest.sections.map((section) => (
            <SectionCard key={section.id} guideId={manifest.id} section={section} />
          ))}
        </div>
      </div>

      <LegalFooter />
    </div>
  );
}
